"use client";
import Link from "next/link";
import { ArrowRight, Award } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { partnerTiers as data } from "@/lib/data/partners";

const tierAccents = ["var(--color-cyan)", "var(--color-purple)", "var(--color-amber)", "var(--color-crimson)"];

export function PartnerTiers() {
  return (
    <Section id="partner-tiers" className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-cyan)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <div className="mt-10 glass rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[rgb(var(--border)/0.18)] bg-[rgb(var(--border)/0.04)]">
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))]">Tier</th>
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))] hidden md:table-cell">Requirements</th>
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))] hidden md:table-cell">Benefits</th>
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))] hidden lg:table-cell">Revenue Model</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, i) => {
                    const c = tierAccents[i % tierAccents.length]!;
                    return (
                      <tr key={row.tier} className="border-b border-[rgb(var(--border)/0.08)]">
                        <td className="px-4 py-3 align-top">
                          <div className="flex items-baseline gap-2">
                            <span className="eyebrow-tiny font-bold" style={{ color: c }}>{String(i + 1).padStart(2, "0")}</span>
                            <span className="font-semibold" style={{ color: c }}>{row.tier}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 align-top text-[rgb(var(--fg-muted))] hidden md:table-cell">{row.requirements}</td>
                        <td className="px-4 py-3 align-top text-[rgb(var(--fg-muted))] hidden md:table-cell">{row.benefits}</td>
                        <td className="px-4 py-3 align-top text-[rgb(var(--fg-muted))] hidden lg:table-cell">{row.model}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 gap-5">
          {data.detail.map((d, i) => {
            const c = tierAccents[i % tierAccents.length]!;
            return (
              <Reveal key={d.name} delay={i * 80}>
                <div className="glass rounded-2xl p-6 h-full" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${c} 12%, transparent)` }}>
                  <div className="flex items-center gap-3">
                    <span className="grid place-items-center h-9 w-9 rounded-lg" style={{ background: `color-mix(in oklch, ${c} 18%, transparent)`, boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${c} 35%, transparent)` }}>
                      <Award className="h-4 w-4" style={{ color: c }} />
                    </span>
                    <span className="eyebrow-tiny font-bold" style={{ color: c }}>{d.name}</span>
                  </div>
                  <p className="mt-3 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{d.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
        <Reveal delay={500}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link key={c.label} href={c.href} className={i === 0 ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm" : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"}>
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
